import { System } from './../../shared-components/System';
import { Injectable, EventEmitter } from '@angular/core';
import { StudentService } from '../../shared-components/student/student.service';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
	GradPlanTypeModel,
	GraduationPlanModel,
	GradPlanCoursesModel,
	GradPlanCreditCodeModel,
	GPStudentNCourseInfoModel,
	GradPlanSubjectAreaModel
} from './graduation-plan.model';
import { GPFilterParam } from './gp-filter-param';
import { YearService } from '../../shared-components/year/year.service';
import { UserService } from '../../shared-components/user/user.service';

@Injectable()
export class GraduationPlanService {

	gpFilterParam: GPFilterParam;
	graduationplanTypes: GradPlanTypeModel[] = [];
	graduationPlanFetch_Complete = new EventEmitter<string>();

	constructor(
		private studentService: StudentService,
		private http: HttpClient,
		private yearService: YearService,
		private userService: UserService,
	) { }

	getgraduationplanData() {
		const queryString = '?studentId=' + this.studentService.selectedStudent.Id +
			'&yearId=' + this.yearService.student360Year.Id +
			'&notInGP=' + this.gpFilterParam.notInGP +
			'&showAllGP=' + true;

		this.http.get<GradPlanTypeModel[]>(System.apiURL.Counselor + 'GetStudentGraduationPlanProgress' + queryString).subscribe(result => {

			// Storing grad plans
			this.graduationplanTypes = result;
			GPFilterParam.planTypes = result;

			// Performing Conversions
			for (let i = 0; i < this.graduationplanTypes.length; i++) {
				this.graduationplanTypes[i] = System.convert(this.graduationplanTypes[i], new GradPlanTypeModel());
				const planType = this.graduationplanTypes[i];
				for (let j = 0; j < planType.GraduationPlans.length; j++) {
					planType.GraduationPlans[j] = System.convert(planType.GraduationPlans[j], new GraduationPlanModel());
					const gradPlan = planType.GraduationPlans[j];
					for (let k = 0; k < gradPlan.SubjectAreas.length; k++) {
						gradPlan.SubjectAreas[k] = System.convert(gradPlan.SubjectAreas[k], new GradPlanSubjectAreaModel());
						const subjectArea = gradPlan.SubjectAreas[k];
						for (let p = 0; p < subjectArea.CreditCodes.length; p++) {
							subjectArea.CreditCodes[p]= System.convert(subjectArea.CreditCodes[p], new GradPlanCreditCodeModel());
						}
					}
				}
			}

			// Sending off data
			this.graduationPlanFetch_Complete.emit('fetched');
		});
	}

	toggleStatusAll(toggle: boolean) {
		const gpLevel: string = this.gpFilterParam.DefaultDisplay;
		this.graduationplanTypes.forEach(function(gpType) {
			gpType.IsExpanded = gpType.GraduationPlans.length > 0 && toggle && gpLevel.indexOf('GP') >= 0;

			gpType.GraduationPlans.forEach(function(gp) {
				gp.IsExpanded = toggle && gpLevel.indexOf('SA') >= 0;

				gp.SubjectAreas.forEach(function(sa) {
					sa.IsExpanded = toggle && gpLevel.indexOf('CC') >= 0;

					sa.CreditCodes.forEach(function(cc) {
						cc.IsExpanded = toggle && gpLevel.indexOf('CR') >= 0;
					});
				});
			});
		});
	}

	getPrimaryGraduationPlan(): GraduationPlanModel {
		let matches = [];
		this.graduationplanTypes.forEach(d => {
			matches = matches.concat(d.GraduationPlans.filter(f => f.IsPrimary));
		});
		return matches[0] ? matches[0] : new GraduationPlanModel();
	}

	getAllCoursesByGradPlanID(gradPlan: number): GradPlanCoursesModel[] {
		let matches: GradPlanCoursesModel[] = [];

		const gradPlanModel: GraduationPlanModel = this.getGraduationPlanById(gradPlan);

		const reducer = (array, item) => array.concat(item['CreditCodes'].map(s => s.Courses));
		matches = gradPlanModel.SubjectAreas
		.reduce(reducer, [])
		.reduce((array, item) => array.concat(item))
		.map(g => {
			g.Course_ID = g.DataBucketScore_ID > 0 ? (-1 * g.DataBucketScore_ID) : g.SSEC_ID;
			return g;
		});
		return matches;
	}

	getGraduationPlanById(gradPlan: number): GraduationPlanModel {
		let matches = [];
		this.graduationplanTypes.forEach(d => {
			matches = matches.concat(d.GraduationPlans.filter(f => f.GradPlan_ID === gradPlan));
		});
		return matches[0];
	}

	getAllDistinctGraduationPlans(): GraduationPlanModel[] {
		let matches: GraduationPlanModel[] = [];

		const reducer = (array, item) => array.concat(item['GraduationPlans'].map(gp => ({ GradPlan_ID: gp.GradPlan_ID, GradPlanName: gp.GradPlanName })));
		matches = this.graduationplanTypes.reduce(reducer, []);

		return matches;
	}

	getGraduationPlanOverrideCreditCodes(gradplanId: number, databucketscoreId: number, ssec_id: number): Observable<GradPlanCreditCodeModel[]> {
		const queryString = '?gradplanId=' + gradplanId +
			'&databucketscoreId=' + databucketscoreId +
			'&ssec_id=' + ssec_id +
			'&ayIdentifier=' + this.yearService.student360Year.AYIdentifier +
			'&siteId=' + this.yearService.student360Year.SchoolID;

		return this.http.get<GradPlanCreditCodeModel[]>(System.apiURL.Student + 'GetGraduationPlanOverrideCreditCodes' + queryString);
	}

	getStudentAndCourseInfo(databucketscoreId: number, ssec_id: number): Observable<GPStudentNCourseInfoModel> {
		const queryString = '?studentId=' + this.studentService.selectedStudent.Id +
			'&databucketscoreId=' + databucketscoreId +
			'&ssec_id=' + ssec_id;

		return this.http.get<GPStudentNCourseInfoModel>(System.apiURL.Student + 'GetStudentAndCourseInfo' + queryString);
	}

	overrideCreditCode(databucketscoreId: number, ssecId: number, gradPlanId: number, creditCode: string): Observable<void> {
        const queryString = '?studentId=' + this.studentService.selectedStudent.Id + '&databucketscoreId=' + databucketscoreId + '&ssecId=' + ssecId + '&gradPlanId= ' + gradPlanId + '&creditCode=' + creditCode;
		return this.http.get<void>(System.apiURL.Student + 'OverrideCreditCode' + queryString);
	}

}
